import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sparkles, ThumbsUp, ThumbsDown, RefreshCw, Loader2, AlertCircle } from 'lucide-react'
import { aiApi } from '@/api/ai'
import { settingsApi } from '@/api/settings'
import { useAiJobPoller, isExplanationResult } from '@/hooks/useAiJobPoller'
import { Button } from '@/components/ui/Button'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import { aiCreditsExceededMessage } from '@/components/ai/AiCreditsIndicator'
import type { PromptType, AiExplanationResult } from '@/types/ai'

interface AiExplanationPanelProps {
  /** Canonical word UUID (words.id, not user_words.id) */
  wordId: string
}

type PanelState = 'idle' | 'loading' | 'done' | 'error'

const PROMPT_LABELS: Record<PromptType, string> = {
  DEFAULT: copy.actions.explainSimply,
  SIMPLER: copy.actions.simpler,
  IN_NATIVE: copy.actions.explainInNative,
  LIKE_A2: 'Like A2',
  LIKE_B2: 'Like B2',
}

const PROMPT_ORDER: PromptType[] = ['IN_NATIVE', 'DEFAULT', 'SIMPLER']

/**
 * AI-11 — shows AI explanation for a word on the WordDetailPage.
 */
export function AiExplanationPanel({ wordId }: AiExplanationPanelProps) {
  const [state, setState] = useState<PanelState>('idle')
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [activePromptType, setActivePromptType] = useState<PromptType>('IN_NATIVE')
  const [result, setResult] = useState<AiExplanationResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<1 | -1 | null>(null)
  const trackedJobIdRef = useRef<string | null>(null)
  const explainGenerationRef = useRef(0)

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.getSettings(),
    staleTime: 60_000,
  })
  const nativeLanguage = settingsQuery.data?.nativeLanguage ?? 'en'

  useAiJobPoller({
    jobId: activeJobId,
    onDone: (response) => {
      if (trackedJobIdRef.current !== response.jobId) return
      trackedJobIdRef.current = null
      setActiveJobId(null)

      if (isExplanationResult(response.result)) {
        setResult(response.result)
        setState('done')
      } else {
        setErrorMsg('Unexpected result type from AI job.')
        setState('error')
      }
    },
    onError: (msg) => {
      trackedJobIdRef.current = null
      setActiveJobId(null)
      setErrorMsg(msg)
      setState('error')
    },
  })

  const explain = async (promptType: PromptType) => {
    const generation = ++explainGenerationRef.current
    setActiveJobId(null)
    setState('loading')
    setActivePromptType(promptType)
    setResult(null)
    setFeedback(null)
    setErrorMsg(null)

    try {
      const res = await aiApi.explainWord({ wordId, promptType, targetLanguage: nativeLanguage })
      if (generation !== explainGenerationRef.current) return
      trackedJobIdRef.current = res.data.jobId
      setActiveJobId(res.data.jobId)
    } catch (e: unknown) {
      if (generation !== explainGenerationRef.current) return
      const axiosErr = e as { response?: { data?: { message?: string }; status?: number } }
      const status = axiosErr?.response?.status
      const msg =
        status === 429
          ? aiCreditsExceededMessage(axiosErr?.response?.data?.message)
          : (axiosErr?.response?.data?.message ?? 'Could not start explanation. Please try again.')
      setErrorMsg(msg)
      setState('error')
    }
  }

  const handleFeedback = async (value: 1 | -1) => {
    if (!result?.explanationId || feedback !== null) return
    setFeedback(value)
    try {
      await aiApi.submitFeedback(result.explanationId, value)
    } catch {
      setFeedback(null)
    }
  }

  const cancel = () => {
    explainGenerationRef.current += 1
    trackedJobIdRef.current = null
    setActiveJobId(null)
    setState('idle')
    setErrorMsg(null)
  }

  return (
    <div className="mb-4 rounded-xl border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {copy.ai.explanationHeading}
        </h3>
      </div>

      {state === 'idle' && (
        <div className="flex flex-wrap gap-2">
          {PROMPT_ORDER.map((promptType) => (
            <Button
              key={promptType}
              className={cn(
                'h-8 w-auto px-4 text-xs',
                promptType === 'IN_NATIVE'
                  ? ''
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
              )}
              onClick={() => explain(promptType)}
            >
              {promptType === 'IN_NATIVE' || promptType === 'DEFAULT' ? (
                <Sparkles className="h-3 w-3" />
              ) : null}
              {PROMPT_LABELS[promptType]}
            </Button>
          ))}
        </div>
      )}

      {state === 'loading' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Generating {PROMPT_LABELS[activePromptType].toLowerCase()} explanation…
          </div>
          <button
            onClick={cancel}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}

      {state === 'done' && result && (
        <div>
          <p className="text-sm leading-relaxed text-foreground">{result.explanation}</p>

          {result.fromCache && (
            <p className="mt-1 text-xs text-muted-foreground/60">Cached response</p>
          )}

          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              {result.explanationId ? (
                <>
                  <button
                    onClick={() => handleFeedback(1)}
                    disabled={feedback !== null}
                    title="Helpful"
                    className={cn(
                      'rounded-md border p-1.5 transition-colors hover:bg-accent disabled:cursor-default',
                      feedback === 1
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'text-muted-foreground',
                    )}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleFeedback(-1)}
                    disabled={feedback !== null}
                    title="Not helpful"
                    className={cn(
                      'rounded-md border p-1.5 transition-colors hover:bg-accent disabled:cursor-default',
                      feedback === -1
                        ? 'border-destructive bg-destructive/10 text-destructive'
                        : 'text-muted-foreground',
                    )}
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                  {feedback !== null && (
                    <span className="ml-1 text-xs text-muted-foreground">Thanks!</span>
                  )}
                </>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              {activePromptType !== 'SIMPLER' && (
                <button
                  onClick={() => explain('SIMPLER')}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Simpler
                </button>
              )}
              {activePromptType !== 'IN_NATIVE' && (
                <button
                  onClick={() => explain('IN_NATIVE')}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  In my language
                </button>
              )}
              {activePromptType !== 'DEFAULT' && (
                <button
                  onClick={() => explain('DEFAULT')}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Explain simply
                </button>
              )}
              <button
                onClick={() => explain(activePromptType)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                title="Regenerate"
              >
                <RefreshCw className="h-3 w-3" />
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {state === 'error' && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
          <Button
            className="h-8 w-auto bg-secondary px-4 text-xs text-secondary-foreground hover:bg-secondary/80"
            onClick={cancel}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  )
}
